/**
 * ReportTypePage
 *
 * One page component covering every report type (Global & Multilateral,
 * Programme Plans, Household Surveys, ..., Research). The active type is
 * derived from the URL path (/reports/type/:reportTypeSlug) via the shared
 * route mapping, so a single component serves every type and re-wires itself
 * when the slug changes.
 *
 * The type restricts the fetch to that type's reports (applied by the reports
 * context's fetchPage based on the resolved sidebar option). An unknown slug
 * resolves to "all-reports", so the page shows All Reports with the All
 * Reports title and description, matching how unknown topic slugs behave in
 * Journal Articles.
 */

import { useLocation } from "react-router-dom"
import ReportsShell from "@/components/reports/ReportsShell"
import ReportsTable, { buildReportFilterOptions } from "@/components/reports/ReportsTable"
import { useReportsPage, ReportsPage } from "@/components/reports/useReportsPage"
import {
  ReportsSidebarOption,
  pathToReportsSidebarOption,
  reportTypeFromOption,
} from "@/components/reports/reportsRoutes"
import { FilterOption } from "@/components/shared/TableFiltersSelector"
import { ReportType, reportTypeLabelMap } from "@/services/reportsService"

/** Short description shown under the page heading for a known report type. */
const PAGE_DESCRIPTION: string = "Browse reports classified as a particular report type"

/**
 * Heading and description used when the slug is unknown and the page falls
 * back to All Reports. These repeat AllReportsPage's values rather than
 * importing them, because page modules export only their component.
 */
const ALL_REPORTS_TITLE: string = "All Reports"
const ALL_REPORTS_DESCRIPTION: string = "Browse reports from the bibliographic sources database"

/**
 * The status filter options. They take no inputs, so they are built once at
 * module load rather than on every render.
 */
const FILTER_OPTIONS: FilterOption[] = buildReportFilterOptions()

/**
 * The report type page (serves every report type via the URL slug).
 *
 * @returns The rendered page element.
 */
export default function ReportTypePage(): React.JSX.Element {
  const location = useLocation()

  // Resolve the sidebar option from the URL path. Using the shared route
  // mapping keeps the slug <-> option convention in one place.
  const sidebarOption: ReportsSidebarOption = pathToReportsSidebarOption(location.pathname)

  // null when the slug is unknown (the option resolved to "all-reports").
  const reportType: ReportType | null = reportTypeFromOption(sidebarOption)

  // One derived value feeds both <title> and <h1> (ReportsShell renders both
  // from its title prop), so they always match.
  const title: string = reportType !== null ? reportTypeLabelMap[reportType] : ALL_REPORTS_TITLE
  const description: string = reportType !== null ? PAGE_DESCRIPTION : ALL_REPORTS_DESCRIPTION

  const page: ReportsPage = useReportsPage({ sidebarOption })

  return (
    <ReportsShell title={title} description={description} showDetailsPanel>
      <ReportsTable page={page} filterOptions={FILTER_OPTIONS} visibleColumns={page.visibleColumns} />
    </ReportsShell>
  )
}
